// The one place the client touches fetch. Cookie mode (plan §5 step 4): the browser sends the
// httpOnly session cookie itself, so requests carry `credentials: 'include'` and no token.
// Every answer is checked: a non-2xx becomes ApiError, a 2xx that is not the promised shape
// becomes ApiError `unexpected_response` (review D-R10), a fetch rejection becomes NetworkError.
import type { z } from 'zod';
import { ApiError, NetworkError, UNEXPECTED_RESPONSE } from './errors.ts';
import { idempotencyKey, ifMatch } from './headers.ts';
import { assertSafePath } from './paths.ts';
import { errorEnvelopeSchema } from './schemas.ts';

export const API_BASE_PATH = '/api/v1';

const HTTP_NO_CONTENT = 204;
const UNEXPECTED_MESSAGE = 'The portal sent an answer this page did not expect. Reload the page.';

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface HttpOptions {
  /** Defaults to `/api/v1` on the page's own origin. */
  readonly baseUrl?: string;
  /** Defaults to the global fetch, looked up on every call so tests can stub it. */
  readonly fetch?: FetchLike;
}

export interface RequestOptions {
  readonly method: HttpMethod;
  readonly path: string;
  readonly body?: unknown;
  /** Record version for `If-Match` (constitution §4.7). */
  readonly version?: number;
  /** `Idempotency-Key` for creates (constitution §4.12). */
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal | undefined;
}

export interface Http {
  /** Sends the request and returns the body parsed by `schema`. */
  json<T>(opts: RequestOptions, schema: z.ZodType<T>): Promise<T>;
  /** Sends the request and requires 204 No Content. */
  noContent(opts: RequestOptions): Promise<void>;
}

function buildHeaders(opts: RequestOptions): Headers {
  const headers = new Headers({ Accept: 'application/json' });
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  if (opts.version !== undefined) headers.set('If-Match', ifMatch(opts.version));
  if (opts.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', idempotencyKey(opts.idempotencyKey));
  }
  return headers;
}

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

async function readJson(res: Response): Promise<unknown> {
  const type = res.headers.get('content-type') ?? '';
  if (!type.toLowerCase().includes('application/json')) return undefined;
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

/** Builds an ApiError from a failed response without trusting its body. */
export async function apiErrorFrom(res: Response): Promise<ApiError> {
  const parsed = errorEnvelopeSchema.safeParse(await readJson(res));
  if (!parsed.success) return new ApiError(res.status, UNEXPECTED_RESPONSE, `HTTP ${res.status}`);
  const { code, message, details } = parsed.data.error;
  return new ApiError(res.status, code, message, details);
}

function unexpected(res: Response): ApiError {
  return new ApiError(res.status, UNEXPECTED_RESPONSE, UNEXPECTED_MESSAGE);
}

export function createHttp(options: HttpOptions = {}): Http {
  const base = options.baseUrl ?? API_BASE_PATH;
  const doFetch: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  async function send(opts: RequestOptions): Promise<Response> {
    const url = `${base}${assertSafePath(opts.path)}`;
    const init: RequestInit = {
      method: opts.method,
      credentials: 'include',
      headers: buildHeaders(opts),
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
      ...(opts.signal === undefined ? {} : { signal: opts.signal }),
    };
    let res: Response;
    try {
      res = await doFetch(url, init);
    } catch (err) {
      if (isAbort(err)) throw err;
      throw new NetworkError(err);
    }
    if (!res.ok) throw await apiErrorFrom(res);
    return res;
  }

  return {
    async json<T>(opts: RequestOptions, schema: z.ZodType<T>): Promise<T> {
      const res = await send(opts);
      const parsed = schema.safeParse(await readJson(res));
      if (!parsed.success) throw unexpected(res);
      return parsed.data;
    },
    async noContent(opts: RequestOptions): Promise<void> {
      const res = await send(opts);
      if (res.status !== HTTP_NO_CONTENT) throw unexpected(res);
    },
  };
}
